import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import ReactFlow, {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Panel,
  getSmoothStepPath,
  getBezierPath,
} from "reactflow";
import "reactflow/dist/style.css";

export default function Diagram({ steps, refreshKey = 0 }) {
  const theme = useTheme();
  const tokens = useMemo(() => theme?.custom ?? {}, [theme]);
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
  const normalizedSteps = useMemo(() => (Array.isArray(steps) ? steps : []), [steps]);
  const { nodes, edges, laneMeta } = useMemo(
    () => layoutSteps(normalizedSteps, tokens, hoveredEdgeId),
    [normalizedSteps, tokens, hoveredEdgeId]
  );

  // Define edgeTypes before any early returns to follow Rules of Hooks
  const edgeTypes = useMemo(
    () => ({ elevatedSmooth: ElevatedSmoothStepEdge }),
    []
  );

  if (normalizedSteps.length === 0) {
    return <div style={{ color: "white", padding: 20 }}>No steps available to render.</div>;
  }

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        borderRadius: theme.shape?.borderRadius || 16,
        overflow: "hidden",
        boxShadow: "0 32px 100px rgba(19,17,60,0.4)",
        background: "linear-gradient(145deg, rgba(44,38,110,0.78), rgba(90,74,160,0.62))",
      }}
    >
      <ReactFlow
        key={refreshKey}
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.28, minZoom: 0.35 }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
        nodesDraggable={false}
        nodesConnectable={false}
        zoomOnScroll
        panOnScroll
        onEdgeMouseEnter={(_, edge) => setHoveredEdgeId(edge?.id || null)}
        onEdgeMouseLeave={() => setHoveredEdgeId(null)}
        proOptions={{ hideAttribution: true }}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{
          type: "elevatedSmooth",
          pathOptions: { shape: "bezier", curvature: 0.28 },
        }}
        style={{ background: "linear-gradient(150deg, rgba(36,32,108,0.75), rgba(92,80,176,0.65))" }}
      >
        <Background variant="dots" gap={32} size={1.3} color="rgba(255,255,255,0.12)" />
        <Controls showInteractive={false} position="bottom-left" />
        {laneMeta.length > 1 && (
          <Panel position="top-right" style={legendStyles(theme)}>
            <span style={{ fontSize: 12, letterSpacing: 1.2, textTransform: "uppercase", color: "rgba(227,232,255,0.65)" }}>
              Lanes
            </span>
            <div style={{ display: "flex", gap: 12 }}>
              {laneMeta.map((lane) => (
                <div key={lane.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: lane.swatch,
                      boxShadow: "0 0 0 1px rgba(255,255,255,0.15), 0 8px 18px rgba(0,0,0,0.35)",
                    }}
                  />
                  <span style={{ fontSize: 13, color: "rgba(220,226,255,0.85)", fontWeight: 600 }}>{lane.name}</span>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}

const nodeWidth = 360; // a bit wider for better readability
const nodeMinHeight = 100; // allow height to grow automatically
const horizontalSpacing = 400; // compensate for wider nodes
const verticalSpacing = 180; // consistent vertical spacing for nice lanes

function ElevatedSmoothStepEdge(props) {
  const {
    id,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    style = {},
    markerEnd,
    label,
    labelStyle = {},
    labelBgStyle = {},
    labelBgPadding = [0, 0],
    interactionWidth,
    pathOptions = {},
    selected,
    animated,
  } = props;

  const shape = pathOptions.shape || "bezier";
  const curvature = pathOptions.curvature ?? 0.28;
  const borderRadius = pathOptions.borderRadius ?? 18;

  const [edgePath, labelX, labelY] =
    shape === "smoothstep"
      ? getSmoothStepPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          borderRadius,
        })
      : getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          curvature,
        });

  const labelYOffset = pathOptions.labelYOffset ?? 0;

  const [paddingY, paddingX] = Array.isArray(labelBgPadding)
    ? [labelBgPadding[0] ?? 0, labelBgPadding[1] ?? labelBgPadding[0] ?? 0]
    : [labelBgPadding ?? 0, labelBgPadding ?? 0];

  const { fill, color, ...restLabelStyle } = labelStyle;
  const mappedLabelStyle = {
    color: color || fill || "rgba(240,244,255,0.96)",
    textShadow: "0 1px 6px rgba(8,10,28,0.55)",
    ...restLabelStyle,
  };

  const mappedBgStyle = mapLabelBackground(labelBgStyle);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={style}
        selected={selected}
        animated={animated}
        interactionWidth={interactionWidth ?? pathOptions.interactionWidth ?? 32}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY + labelYOffset}px)`,
              pointerEvents: "none",
              zIndex: 1000,
              ...mappedLabelStyle,
            }}
            className="rf-elevated-edge-label"
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: `${paddingY}px ${paddingX}px`,
                ...mappedBgStyle,
              }}
            >
              {label}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

function mapLabelBackground(style = {}) {
  const { fill, stroke, strokeWidth, rx, ry, filter, backdropFilter, ...rest } = style;
  const borderRadius = rx ?? ry ?? 10;
  const border = stroke ? `${strokeWidth ?? 1}px solid ${stroke}` : undefined;
  return {
    background: fill ?? "rgba(13,16,40,0.52)",
    border,
    borderRadius,
    filter,
    backdropFilter: backdropFilter ?? "blur(12px)",
    boxShadow: "0 8px 28px rgba(6,9,28,0.45)",
    ...rest,
  };
}

function soften(color, amount = 0.65) {
  if (!color) return `rgba(255,255,255,${amount})`;
  if (color.startsWith("rgba")) {
    const parts = color
      .replace("rgba", "")
      .replace("(", "")
      .replace(")", "")
      .split(",")
      .map((p) => parseFloat(p.trim()));
    if (parts.length < 4) return color;
    const alpha = Math.max(0, Math.min(1, amount));
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  }
  return hexToRgba(color, amount);
}

function hexToRgba(hex = "", alpha = 1) {
  const value = hex.replace("#", "");
  if (value.length !== 3 && value.length !== 6) {
    return hex.startsWith("rgba") ? hex : `rgba(185,189,252,${alpha})`;
  }
  const normalized = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const int = parseInt(normalized, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function legendStyles(theme) {
  const glass = theme?.custom?.glassSoft || {};
  return {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "12px 18px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.16)",
    ...glass,
    background: "linear-gradient(155deg, rgba(255,255,255,0.24), rgba(176,166,255,0.26))",
    boxShadow: "0 16px 42px rgba(24,20,70,0.32)",
  };
}

function inferLaneFromLabel(label) {
  const text = String(label || "").toLowerCase();
  if (text.includes("ccm")) return "CCM";
  if (text.includes("rpm")) return "RPM";
  if (text.includes("bhi")) return "BHI";
  return null;
}

function layoutSteps(steps, tokens = {}, hoveredEdgeId = null) {
  const nodeMap = new Map();
  steps.forEach((s) => nodeMap.set(s.id, s));

  // incoming edge counts (support arbitrary branches)
  const incoming = new Map(steps.map((s) => [s.id, 0]));
  steps.forEach((s) => {
    if (s.type === "decision" && s.branches && typeof s.branches === "object") {
      Object.values(s.branches).forEach((to) => {
        if (to && incoming.has(to)) incoming.set(to, incoming.get(to) + 1);
      });
    }
    if (s.next && incoming.has(s.next)) incoming.set(s.next, incoming.get(s.next) + 1);
  });

  // queue roots; fallback to first step
  const queue = [];
  incoming.forEach((c, id) => {
    if (c === 0) queue.push(id);
  });
  if (queue.length === 0 && steps.length > 0) queue.push(steps[0].id);

  // topological-ish leveling
  const levelMap = new Map();
  queue.forEach((id) => levelMap.set(id, 0));
  const seen = new Set();
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const step = nodeMap.get(id);
    const level = levelMap.get(id) || 0;
    const push = (childId) => {
      if (!childId || !nodeMap.has(childId)) return;
      const nextLevel = level + 1;
      const existing = levelMap.get(childId);
      if (existing === undefined || nextLevel > existing) levelMap.set(childId, nextLevel);
      queue.push(childId);
    };
    if (step?.type === "decision" && step.branches) {
      Object.values(step.branches).forEach(push);
    }
    if (step?.next) push(step.next);
  }

  steps.forEach((s) => {
    if (!levelMap.has(s.id)) levelMap.set(s.id, 0);
  });

  const propagateForward = () => {
    const maxDepth = Math.max(1, steps.length);
    for (let i = 0; i < maxDepth; i += 1) {
      let changed = false;
      steps.forEach((step) => {
        const sourceLevel = levelMap.get(step.id) ?? 0;
        const enforceForward = (childId) => {
          if (!childId || !levelMap.has(childId)) return;
          const current = levelMap.get(childId) ?? 0;
          const desired = Math.min(maxDepth, Math.max(current, sourceLevel + 1));
          if (desired > current) {
            levelMap.set(childId, desired);
            changed = true;
          }
        };
        if (step.type === "decision" && step.branches && typeof step.branches === "object") {
          Object.values(step.branches).forEach(enforceForward);
        }
        if (step.next) enforceForward(step.next);
      });
      if (!changed) break;
    }
  };

  propagateForward();

  // normalize lowest level to zero to keep layout compact
  const levelValues = Array.from(levelMap.values());
  const minLevel = levelValues.length > 0 ? Math.min(...levelValues) : 0;
  if (Number.isFinite(minLevel) && minLevel !== 0) {
    levelMap.forEach((lvl, id) => levelMap.set(id, lvl - minLevel));
  }

  // lane buckets (optional)
  const lanes = Array.from(
    new Set(steps.map((s) => String(s.lane ?? inferLaneFromLabel(s.label) ?? "Main")))
  ).sort();
  const laneIndex = (lane) => lanes.indexOf(lane);

  // sibling ordering per level
  const levelBuckets = new Map();
  levelMap.forEach((lvl, id) => {
    if (!levelBuckets.has(lvl)) levelBuckets.set(lvl, []);
    levelBuckets.get(lvl).push(id);
  });
  levelBuckets.forEach((arr) => arr.sort());

  // Natural sort helper for ids like "1", "2", "10" without breaking non-numeric ids
  const naturalCompareIds = (a, b) => {
    const na = Number(a), nb = Number(b);
    const aNum = !Number.isNaN(na);
    const bNum = !Number.isNaN(nb);
    if (aNum && bNum) return na - nb;
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a).localeCompare(String(b));
  };

  // Ensure stable ordering inside levels
  levelBuckets.forEach((arr) => arr.sort(naturalCompareIds));

  const nodeLane = new Map();
  const nodes = steps.map((s) => {
    const level = levelMap.get(s.id) ?? 0;
    const siblings = levelBuckets.get(level) || [s.id];
    const index = siblings.indexOf(s.id);
    const offset = (siblings.length - 1) / 2;
    const lane = String(s.lane ?? inferLaneFromLabel(s.label) ?? "Main");
    const yBase = laneIndex(lane) * (verticalSpacing * 2);
    const x = level * horizontalSpacing;
    const y = yBase + (index - offset) * verticalSpacing;

    const fallbackLane = tokens.lanes?.Main || { tint: "#b9bdfc", stroke: "rgba(185,189,252,0.6)" };
    const laneColors = tokens.lanes && tokens.lanes[lane] ? { ...fallbackLane, ...tokens.lanes[lane] } : fallbackLane;
    nodeLane.set(s.id, { lane, laneColors });
    const laneGlow = hexToRgba(laneColors.tint || fallbackLane.tint, 0.18);

    return {
      id: s.id,
      data: { label: s.label, title: s.label },
      position: { x, y },
      style: {
        width: nodeWidth,
        minHeight: nodeMinHeight,
        borderRadius: 20,
        padding: "16px 18px",
        // Glassmorphism via theme tokens (soft for regular, strong for decision nodes)
        ...(s.type === "decision" ? (tokens.glassStrong || {}) : (tokens.glassSoft || {})),
        // Fallback colors if tokens are unavailable and subtle lane accent
        border: `1px solid ${soften(laneColors.stroke || "rgba(255,255,255,0.18)", 0.7)}`,
        // Multi-layer shadow: soft ambient + lane-tinted glow + inner sheen
        boxShadow: `0 22px 64px rgba(18,16,60,0.36), 0 0 0 1px ${soften(laneColors.stroke || "rgba(255,255,255,0.2)", 0.65)}, 0 28px 58px ${laneColors.shadow || hexToRgba(laneColors.tint, 0.3)}, inset 0 0 0 1px rgba(255,255,255,0.1)`,
        background: "linear-gradient(160deg, rgba(255,255,255,0.32), rgba(220,212,255,0.2))",
        backgroundImage: `linear-gradient(170deg, rgba(247,245,255,0.22), rgba(204,197,255,0.14)), radial-gradient(140% 140% at 20% 25%, ${laneGlow}, transparent 62%)`,
        color: "rgba(242,246,255,0.95)",
        fontSize: 18,
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: 0.12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflow: "visible",
  textShadow: "none",
        // Pretty lane-aware border accent (subtle)
        borderImageSlice: 1,
        borderImageSource: `conic-gradient(from 120deg, ${laneColors.stroke || "rgba(185,189,252,0.6)"}, rgba(255,255,255,0.25), ${laneColors.stroke || "rgba(185,189,252,0.6)"})`,
      },
      sourcePosition: "right",
      targetPosition: "left",
    };
  });

  const edges = [];
  steps.forEach((s) => {
    const laneInfo = nodeLane.get(s.id);
    const laneTint = laneInfo?.laneColors?.tint || tokens.lanes?.Main?.tint;
    const baseStroke = soften(laneTint || "#b9bdfc", 0.64);
    const hoverStroke = soften(laneTint || "#b9bdfc", 0.86);
    const sourceLevel = levelMap.get(s.id) ?? 0;
    const sourceLaneIndex = laneInfo ? laneIndex(laneInfo.lane) : 0;
    if (s.type === "decision" && s.branches && typeof s.branches === "object") {
      Object.entries(s.branches).forEach(([label, to]) => {
        if (!to) return;
        const edgeId = `${s.id}-${label}`;
        const isHovered = hoveredEdgeId === edgeId;
        const targetLaneInfo = nodeLane.get(to);
        const targetLevel = levelMap.get(to) ?? sourceLevel + 1;
        const laneGap = targetLaneInfo ? Math.abs(sourceLaneIndex - laneIndex(targetLaneInfo.lane)) : 0;
        const depthGap = Math.max(0, targetLevel - sourceLevel);
        const curvature = 0.18 + laneGap * 0.05 + depthGap * 0.03;
        const labelOffset = -28 - laneGap * 6;
        edges.push({
          id: edgeId,
          source: s.id,
          target: to,
          label,
          type: "elevatedSmooth",
          markerEnd: { type: "arrowclosed", color: isHovered ? hoverStroke : baseStroke },
          style: {
            stroke: isHovered ? hoverStroke : baseStroke,
            strokeWidth: isHovered ? 3.6 : 3.2,
            opacity: isHovered ? 0.88 : 0.7,
          },
          pathOptions: { shape: "bezier", curvature, labelYOffset: labelOffset },
          labelStyle: {
            fill: "rgba(240,244,255,0.96)",
            fontSize: 14,
            fontWeight: 600,
          },
          // Always use a dark-glass pill for connector labels; intensify subtly on hover
          labelBgStyle: isHovered
            ? {
                fill: "rgba(13,16,40,0.68)",
                stroke: hoverStroke,
                strokeWidth: 1,
                rx: 9,
                ry: 9,
              }
            : {
                fill: "rgba(13,16,40,0.5)",
                stroke: baseStroke,
                strokeWidth: 1,
                rx: 9,
                ry: 9,
              },
          labelBgPadding: [12, 14],
        });
      });
    }
    if (s.next) {
      const targetLevel = levelMap.get(s.next) ?? sourceLevel + 1;
      const depthGap = Math.max(0, targetLevel - sourceLevel);
      const targetLaneInfo = nodeLane.get(s.next);
      const laneGap = targetLaneInfo ? Math.abs(sourceLaneIndex - laneIndex(targetLaneInfo.lane)) : 0;
      const curvature = 0.16 + laneGap * 0.045 + Math.max(0, depthGap - 1) * 0.02;
      edges.push({
        id: `${s.id}-${s.next}`,
        source: s.id,
        target: s.next,
        type: "elevatedSmooth",
        markerEnd: { type: "arrowclosed", color: baseStroke },
        style: { stroke: baseStroke, strokeWidth: 3.1, opacity: 0.62 },
        pathOptions: { shape: "bezier", curvature },
        labelBgStyle: { fill: "transparent", stroke: "transparent" },
      });
    }
  });

  const laneMeta = lanes.map((lane) => {
    const fallbackLane = tokens.lanes?.Main || { tint: "#b9bdfc", stroke: "rgba(185,189,252,0.6)" };
    const laneColors = tokens.lanes && tokens.lanes[lane] ? { ...fallbackLane, ...tokens.lanes[lane] } : fallbackLane;
    return {
      name: lane,
      swatch: `linear-gradient(135deg, ${hexToRgba(laneColors.tint, 0.85)}, ${hexToRgba(
        laneColors.stroke || laneColors.tint,
        0.65
      )})`,
    };
  });

  return { nodes, edges, laneMeta };
}
